/**
 * Ticket vocabulary shared by every tickets surface — mirrors the backend's
 * ``app/models/ticket.py`` (statuses, Linear's 0–4 priority scale, relation
 * types) so the list, the sheet, and the filters never disagree on a label.
 */

import { formatDistance, formatDistanceStrict, isAfter, isBefore, parseISO } from "date-fns";

import { BUSINESS_TZ } from "@/lib/local-date";
import type {
  ActorKind,
  CaseCategory,
  CaseSystem,
  HandoffSystem,
  RelationDirection,
  RelationType,
  RoutingStatus,
  SuggestedHandler,
  TargetBudget,
  TicketEvent,
  TicketEventKind,
  TicketPriority,
  TicketSort,
  TicketSortDirection,
  TicketStatus,
  TicketSummary,
} from "@/services/tickets-services";

export type StatusCategory = "triage" | "backlog" | "unstarted" | "started" | "completed" | "canceled";

/** Board order — the same order Linear renders its workflow states. */
export const TICKET_STATUSES: readonly TicketStatus[] = [
  "triage",
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "done",
  "canceled",
  "duplicate",
];

export const STATUS_META: Record<TicketStatus, { label: string; category: StatusCategory }> = {
  triage: { label: "Triage", category: "triage" },
  backlog: { label: "Backlog", category: "backlog" },
  todo: { label: "Todo", category: "unstarted" },
  in_progress: { label: "In Progress", category: "started" },
  in_review: { label: "In Review", category: "started" },
  done: { label: "Done", category: "completed" },
  canceled: { label: "Canceled", category: "canceled" },
  duplicate: { label: "Duplicate", category: "canceled" },
};

/** Everything that still needs work — the list's default status filter. */
export const OPEN_STATUSES: readonly TicketStatus[] = TICKET_STATUSES.filter(
  (s) => !["completed", "canceled"].includes(STATUS_META[s].category),
);

export const TICKET_PRIORITIES: readonly TicketPriority[] = [0, 1, 2, 3, 4];

export const PRIORITY_META: Record<TicketPriority, { label: string; short: string }> = {
  0: { label: "No priority", short: "—" },
  1: { label: "Urgent", short: "P1" },
  2: { label: "High", short: "P2" },
  3: { label: "Medium", short: "P3" },
  4: { label: "Low", short: "P4" },
};

/** The first click on a column. Dates and case numbers open newest-first;
 *  text opens A→Z; status opens at Triage; priority opens at Urgent. */
export const TICKET_SORT_DEFAULT_DIR: Record<TicketSort, TicketSortDirection> = {
  number: "desc",
  title: "asc",
  status: "asc",
  handler: "asc",
  priority: "asc",
  assignee: "asc",
  created: "desc",
  updated: "desc",
};

const TICKET_SORTS: readonly TicketSort[] = [
  "number",
  "title",
  "status",
  "handler",
  "priority",
  "assignee",
  "created",
  "updated",
];

export function isTicketSort(value: string | null): value is TicketSort {
  return value != null && (TICKET_SORTS as readonly string[]).includes(value);
}

export interface RelationPhrasing {
  type: RelationType;
  direction: RelationDirection;
  /** Reads as "This ticket <label> <other ticket>". */
  label: string;
}

/**
 * The five ways a user can phrase a relation from the ticket they're looking
 * at. The inverse phrasings ("is blocked by") submit ``direction: "incoming"``,
 * which the server stores as the OTHER ticket being the source — so there is
 * exactly one stored shape per fact and the UI never has to flip anything.
 */
export const RELATION_PHRASINGS: readonly RelationPhrasing[] = [
  { type: "blocks", direction: "outgoing", label: "blocks" },
  { type: "blocks", direction: "incoming", label: "is blocked by" },
  { type: "duplicates", direction: "outgoing", label: "duplicates" },
  { type: "duplicates", direction: "incoming", label: "is duplicated by" },
  { type: "relates_to", direction: "outgoing", label: "relates to" },
];

export const SYMMETRIC_RELATION_TYPES: ReadonlySet<RelationType> = new Set<RelationType>(["relates_to"]);

/** The case key — ``HI-10042`` — the one string that names a case everywhere
 *  (list, sheet, Kory, Slack, vendor email subjects, Linear titles). */
export function formatTicketNumber(n: number): string {
  return `HI-${n}`;
}

/** Who works the case. Set (by triage, or by Kory from its intake), it is where the routing agent sends it. */
export const HANDLERS: readonly SuggestedHandler[] = ["internal_it", "networkdr", "extel", "ai_team"];

export const HANDLER_META: Record<SuggestedHandler, { label: string; hint: string }> = {
  internal_it: { label: "Internal IT", hint: "Emailed to Hickory's IT help inbox as the requester." },
  networkdr: { label: "NetworkDr", hint: "Emailed to the IT vendor as the requester." },
  extel: { label: "Extel", hint: "Emailed to the phone vendor as the requester." },
  ai_team: { label: "Hickory AI team", hint: "Opens a Linear issue for the dev team." },
};

/** The create dialog and sheet offer this in place of a handler: the agent decides. */
export const HANDLER_AUTO = "auto";

/** Which product the case is about — the same vocabulary Kory's intake and the
 *  page-feedback modal use, so a filter on the list means one thing wherever the
 *  case came from. Menu order: Hickory's own products, then the tools Hickory
 *  administers, then the physical stuff. */
export const CASE_SYSTEMS: readonly CaseSystem[] = [
  "hickoryos",
  "kory_browser",
  "kory_slack",
  "kory_claude_desktop",
  "kory_code",
  "claude",
  "servicetitan",
  "pipedrive",
  "netsuite",
  "ringcentral",
  "verizon",
  "slack",
  "google_workspace",
  "microsoft_365",
  "paychex",
  "device",
  "network",
  "other",
];

export const SYSTEM_META: Record<CaseSystem, { label: string }> = {
  hickoryos: { label: "HickoryOS" },
  kory_browser: { label: "Kory in browser at go.hickory.ai" },
  kory_slack: { label: "Kory in Slack" },
  kory_claude_desktop: { label: "Kory in Claude Desktop" },
  kory_code: { label: "Kory Code" },
  claude: { label: "Claude" },
  servicetitan: { label: "ServiceTitan" },
  pipedrive: { label: "Pipedrive" },
  netsuite: { label: "NetSuite" },
  ringcentral: { label: "RingCentral" },
  verizon: { label: "Verizon (mobile phone)" },
  slack: { label: "Slack" },
  google_workspace: { label: "Google Workspace" },
  microsoft_365: { label: "Microsoft 365" },
  paychex: { label: "Paychex" },
  device: { label: "Computer, tablet or other hardware" },
  network: { label: "Internet or network" },
  other: { label: "Other" },
};

/** What kind of ask the case is. Getting INTO something (login, access) comes
 *  first, then what is wrong INSIDE it. */
export const CASE_CATEGORIES: readonly CaseCategory[] = [
  "login",
  "new_access",
  "access_change",
  "outage",
  "bug",
  "question",
  "feature_request",
  "config_change",
  "training_request",
  "other",
];

/** The kinds of ask that make sense about a page — what "Feedback For This
 *  Page" offers. Getting into something is an IT case, not page feedback. */
export const PAGE_FEEDBACK_CATEGORIES: readonly CaseCategory[] = ["bug", "question", "feature_request"];

export const CATEGORY_META: Record<CaseCategory, { label: string; hint: string }> = {
  login: { label: "Can't log in or locked out", hint: "You have an account but can't get in." },
  new_access: { label: "New access", hint: "An account, seat or permission someone doesn't have yet." },
  access_change: { label: "Access change or removal", hint: "Change what an existing account can do, or remove it." },
  outage: { label: "Outage or not working", hint: "Down, unreachable or failing for everyone." },
  bug: { label: "Bug or wrong data", hint: "It works, but not the way it should — an error, a wrong number." },
  question: { label: "Question or how-to", hint: "Nothing is broken; you need to know how." },
  feature_request: { label: "Feature request", hint: "Something that doesn't exist yet." },
  config_change: { label: "Configuration change", hint: "A setting, threshold or mapping on something that works." },
  training_request: { label: "Training request", hint: "Someone needs to be shown how to use something that works." },
  other: { label: "Something else", hint: "None of the above fits." },
};

/** The durations the sheet offers for time spent, in minutes. Stored as a number
 *  so cases sum and average; presets keep entries comparable. */
export const TIME_SPENT_MINUTES: readonly number[] = [
  15, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720, 960, 1440, 2400,
];

/** "15 min", "1 hr", "1 hr 30 min", "40 hr". */
export function formatTimeSpent(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

// Membership in the vocabulary lists, not `in META`: `in` also answers yes to
// inherited names ("constructor"), and a shared URL carrying one would reach the
// API as a filter value it rejects, putting the list in its error state.
export function isCaseSystem(v: string | null | undefined): v is CaseSystem {
  return !!v && (CASE_SYSTEMS as readonly string[]).includes(v);
}

export function isCaseCategory(v: string | null | undefined): v is CaseCategory {
  return !!v && (CASE_CATEGORIES as readonly string[]).includes(v);
}

export function isSuggestedHandler(v: string | null | undefined): v is SuggestedHandler {
  return !!v && (HANDLERS as readonly string[]).includes(v);
}

/** The label Kory writes above the long-form part of a case body. What follows
 *  it, up to the `---` intake rule, is detail a handler rarely needs, so the
 *  sheet folds it away; Linear and the vendor emails, which take the body
 *  verbatim, show it as a labeled section. */
export const FULL_CONTEXT_LABEL = "**Full context**";

/** A case body in three parts: the words a handler works from, the folded
 *  long form (null when the body has none), and the intake rows and footer. */
export function splitFullContext(description: string): { lead: string; fullContext: string | null; tail: string } {
  const marker = `\n${FULL_CONTEXT_LABEL}\n`;
  const at = description.indexOf(marker);
  if (at === -1) return { lead: description, fullContext: null, tail: "" };
  const lead = description.slice(0, at).trimEnd();
  const rest = description.slice(at + marker.length);
  // The intake rule is the LAST one: the rows and footer follow it, and the
  // long form itself may carry a pasted rule.
  const rule = rest.lastIndexOf("\n---\n");
  if (rule === -1) return { lead, fullContext: rest.trim(), tail: "" };
  return { lead, fullContext: rest.slice(0, rule).trim(), tail: rest.slice(rule).trim() };
}

export const ROUTING_STATUS_META: Record<RoutingStatus, { label: string; settled: boolean }> = {
  pending: { label: "Routing…", settled: false },
  routing: { label: "Routing…", settled: false },
  routed: { label: "Routed", settled: true },
  failed: { label: "Routing failed", settled: true },
  held: { label: "Held", settled: true },
};

/** Where an external ticket lives — a handoff, as distinct from a case-to-case relation. */
export const HANDOFF_SYSTEMS: readonly HandoffSystem[] = ["linear", "networkdr", "extel", "internal_it", "other"];

export const HANDOFF_SYSTEM_META: Record<HandoffSystem, { label: string }> = {
  linear: { label: "Linear" },
  networkdr: { label: "NetworkDr" },
  extel: { label: "Extel" },
  internal_it: { label: "Hickory IT" },
  other: { label: "Other" },
};

/** Badge text for a journal kind. A kind this bundle does not know yet — the
 *  backend shipped one first — renders as the raw slug instead of throwing. */
export function eventKindLabel(kind: string): string {
  if (Object.prototype.hasOwnProperty.call(EVENT_KIND_META, kind)) {
    return EVENT_KIND_META[kind as TicketEventKind].label;
  }
  return kind;
}

export const EVENT_KIND_META: Record<TicketEventKind, { label: string }> = {
  comment: { label: "Comment" },
  agent_note: { label: "Router" },
  routing: { label: "Routing" },
  handoff: { label: "Handoff" },
  email: { label: "Email" },
  assignment: { label: "Assignee" },
  sla: { label: "Target" },
  external_comment: { label: "External" },
  reopened: { label: "Reopened" },
  attachment: { label: "Attachment" },
};

export interface ExternalCommentSource {
  author: string;
  via: string;
  url: string | null;
  /** An integration (GitHub, an agent workflow) left it, not a person. */
  isBot: boolean;
}

/** Who wrote a comment mirrored from a handoff's other side, and where — "Ada L"
 *  via "Linear" — read from the event's ``meta`` (``author``, ``system``, ``url``, ``is_bot``). */
export function externalCommentSource(meta: TicketEvent["meta"]): ExternalCommentSource {
  const author = typeof meta?.author === "string" && meta.author.trim() ? meta.author : "Someone";
  const system = typeof meta?.system === "string" ? meta.system : null;
  const via = isHandoffSystem(system) ? HANDOFF_SYSTEM_META[system].label : EVENT_KIND_META.external_comment.label;
  const url = typeof meta?.url === "string" && meta.url ? meta.url : null;
  return { author, via, url, isBot: meta?.is_bot === true };
}

export function isClosedStatus(status: TicketStatus): boolean {
  return !OPEN_STATUSES.includes(status);
}

export type TargetTone = "unset" | "pending" | "overdue" | "met" | "on_time" | "late";

export interface TargetReading {
  text: string;
  tone: TargetTone;
}

// Targets are business time (the policy counts Eastern business hours), so they
// read in Eastern for everyone — a Colorado user and a New York user see the same
// "by Sep 24, 12:00 PM ET".
const STAMP = new Intl.DateTimeFormat("en-US", {
  timeZone: BUSINESS_TZ,
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** An instant as the targets name it — "Sep 24, 12:00 PM ET". */
export function stampTarget(iso: string): string {
  return `${STAMP.format(parseISO(iso))} ET`;
}

/** The outcome of a target, read against the moment it was met: "Assigned Sep 23,
 *  3:10 PM ET · on time", "Due in about 3 hours", "2 days overdue". The target itself
 *  (its budget and exact time) is a separate line — see ``describeBudget`` — so it
 *  stays visible in every state. ``met`` without a ``metAt`` is a case older than
 *  the stamps — it happened, when is unknown — and reads as the bare verb. */
export function readTarget(
  verb: string,
  dueAt: string | null,
  met: boolean,
  metAt: string | null,
  now: Date = new Date(),
): TargetReading {
  if (met) {
    if (!metAt) return { text: verb, tone: "met" };
    if (!dueAt) return { text: `${verb} ${stampTarget(metAt)}`, tone: "met" };
    const metOn = parseISO(metAt);
    const due = parseISO(dueAt);
    if (!isAfter(metOn, due)) return { text: `${verb} ${stampTarget(metAt)} · on time`, tone: "on_time" };
    return { text: `${verb} ${stampTarget(metAt)} · ${formatDistanceStrict(due, metOn)} late`, tone: "late" };
  }
  if (!dueAt) return { text: "—", tone: "unset" };
  const due = parseISO(dueAt);
  if (isBefore(due, now)) return { text: `${formatDistanceStrict(due, now)} overdue`, tone: "overdue" };
  return { text: `Due ${formatDistance(due, now, { addSuffix: true })}`, tone: "pending" };
}

const BUDGET_NOTES: Record<TargetBudget["source"], string> = {
  policy: "",
  needed_by: " · the date it's needed by",
  by_hand: " · set by hand",
  earlier_lane: " · from an earlier lane or policy",
};

/** The promise behind a target, kept visible whatever the outcome: "Target: 15 business
 *  minutes from filing · Sep 23, 7:03 PM ET", with a note when it isn't the current policy's
 *  own number — pulled in to the date the filer needs it by, moved by hand, or stamped under a
 *  lane or a policy the case has since left. */
export function describeBudget(budget: TargetBudget, dueAt: string): string {
  return `Target: ${budget.label} from filing · ${stampTarget(dueAt)}${BUDGET_NOTES[budget.source] ?? ""}`;
}

/** The assignment target is met by the FIRST assignment; a case that has an assignee
 *  but no stamp predates the stamps. */
export function assignmentTarget(ticket: TicketSummary, now?: Date): TargetReading {
  const met = ticket.first_assigned_at != null || ticket.assignee != null;
  return readTarget("Assigned", ticket.assign_due_at, met, ticket.first_assigned_at, now);
}

/** The resolution target is met when the status enters a closed state. */
export function resolutionTarget(ticket: TicketSummary, now?: Date): TargetReading {
  const met = ticket.resolved_at != null || isClosedStatus(ticket.status);
  return readTarget("Resolved", ticket.resolve_due_at, met, ticket.resolved_at, now);
}

export const ACTOR_LABEL: Record<ActorKind, string> = {
  user: "",
  agent: "Routing agent",
  system: "System",
};

/** One row of a comment event's ``meta.deliveries`` — where the backend sent the comment.
 *  ``ok`` is null while the send is in flight (recorded before it leaves). */
interface Delivery {
  kind: "participant" | "handoff" | "slack";
  system?: string | null;
  to?: string | null;
  ok: boolean | null;
}

function isHandoffSystem(v: string | null | undefined): v is HandoffSystem {
  return !!v && v in HANDOFF_SYSTEM_META;
}

/** "Sent to 2 people, NetworkDr, Linear TEST-9 · 2 on Slack" (plus "· 1 sending"
 *  while a send is in flight, "· 1 not delivered" when one was refused) — or
 *  null when the comment carries no deliveries. A Slack DM goes to someone the
 *  comment also emails, so a DM that doesn't land is not counted as not delivered. */
export function deliverySummary(meta: TicketEvent["meta"]): string | null {
  const deliveries = Array.isArray(meta?.deliveries) ? (meta.deliveries as Delivery[]) : [];
  if (deliveries.length === 0) return null;
  const sent = deliveries.filter((d) => d.ok === true);
  const inFlight = deliveries.filter((d) => d.ok == null).length;
  const people = sent.filter((d) => d.kind === "participant").length;
  const onSlack = sent.filter((d) => d.kind === "slack").length;
  const places = sent
    .filter((d) => d.kind === "handoff")
    .map((d) => {
      if (d.system === "linear") return `Linear ${d.to ?? ""}`.trim();
      return isHandoffSystem(d.system) ? HANDOFF_SYSTEM_META[d.system].label : (d.system ?? "");
    });
  const parts = [...(people > 0 ? [`${people} ${people === 1 ? "person" : "people"}`] : []), ...places];
  const failed = deliveries.filter((d) => d.ok === false && d.kind !== "slack").length;
  const head =
    parts.length > 0 ? `Sent to ${parts.join(", ")}` : onSlack > 0 ? "Sent" : inFlight > 0 ? "Sending" : "Not sent";
  const tail = [
    ...(onSlack > 0 ? [`${onSlack} on Slack`] : []),
    ...(inFlight > 0 ? [`${inFlight} sending`] : []),
    ...(failed > 0 ? [`${failed} not delivered`] : []),
  ];
  return tail.length > 0 ? `${head} · ${tail.join(" · ")}` : head;
}

export function isTicketStatus(v: string | null | undefined): v is TicketStatus {
  return !!v && (TICKET_STATUSES as readonly string[]).includes(v);
}

export function isTicketPriority(v: number): v is TicketPriority {
  return Number.isInteger(v) && v >= 0 && v <= 4;
}

export type TextSegment = { kind: "text"; text: string } | { kind: "link"; text: string; href: string };

// An http(s) URL, minus trailing sentence punctuation (".", ",", ")" …) so
// "see https://x.test/pr/1." links the URL and leaves the period as text. A
// balanced "(…)" stays inside (".../Function_(mathematics)"); a lone ")" does not.
const URL_RE = /https?:\/\/(?:[^\s<>"'()]|\([^\s<>"'()]*\))*(?:[^\s<>"'().,;:!?\]}]|\([^\s<>"'()]*\))/gi;

/** Split a plain-text comment into text and URL segments; the text is never reinterpreted. */
export function splitLinks(body: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let at = 0;
  for (const m of body.matchAll(URL_RE)) {
    if (m.index > at) segments.push({ kind: "text", text: body.slice(at, m.index) });
    segments.push({ kind: "link", text: m[0], href: m[0] });
    at = m.index + m[0].length;
  }
  if (at < body.length) segments.push({ kind: "text", text: body.slice(at) });
  return segments;
}
